import React from "react";
import EmptyView from "@view/EmptyView";
import * as Option from "@utils/Option";
import * as styles from "@styles/PageCounter.css";
import type {
  PageCounterErrorPayload,
  PageCounterGuidancePayload,
  PageCounterNonPdfPayload,
  PageCounterPhase,
  PageCounterPhasePayload,
  PageCounterProcessingPayload,
  PageCounterRouteMissingPayload,
  PageCounterSelectingPayload,
  PageCounterSuccessPayload,
} from "./PageCounterPhase";

export type PageCounterPanelProps = {
  readonly [K in PageCounterPhase]: {
    readonly phase: K;
    readonly payload: PageCounterPhasePayload[K];
  };
}[PageCounterPhase];

const idleView = <EmptyView />;

const guidanceView = (payload: PageCounterGuidancePayload): React.ReactElement => (
  <div className={styles.statusCard} role="note">
    <p className={styles.statusLead}>{payload.guidanceText}</p>
  </div>
);

const selectingView = (payload: PageCounterSelectingPayload): React.ReactElement => (
  <div className={styles.statusCard} role="status">
    <p className={styles.statusLead}>
      {Option.fold(payload.selectedFileName, () => "Select a PDF to count.", (fileName) => `Selected: ${fileName}`)}
    </p>
  </div>
);

const processingView = (payload: PageCounterProcessingPayload): React.ReactElement => (
  <div className={styles.statusCard} role="status">
    <p className={styles.statusLead}>{payload.progressLabel}</p>
    <p className={styles.statusText}>{payload.selectedFileName}</p>
  </div>
);

const formatMoney = (cents: number, currency: string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(cents / 100);

const successView = (payload: PageCounterSuccessPayload): React.ReactElement => {
  const unitPrice = formatMoney(payload.unitPriceCents, payload.currency);
  const totalCost = formatMoney(payload.totalCostCents, payload.currency);

  return (
    <div className={styles.statusCardSuccess} role="status">
      <p className={styles.statusLead}>Count complete for {payload.selectedFileName}.</p>
      <p className={styles.statusText}>{payload.message}</p>
      <div className={styles.resultGrid}>
        <p className={styles.resultItem}>Document: {payload.documentId}</p>
        <p className={styles.resultItem}>MIME type: {payload.mimeType}</p>
        <p className={styles.resultItem}>
          Estimated cost: {totalCost} ({payload.billablePages} pages at {unitPrice}/page)
        </p>
        <p className={styles.resultItem}>Estimate status: {payload.billingOutcome}</p>
        <p className={styles.resultItem}>Trace: {payload.traceId}</p>
      </div>
    </div>
  );
};

const routeMissingView = (payload: PageCounterRouteMissingPayload): React.ReactElement => (
  <div className={styles.statusCardError} role="alert">
    <p className={styles.statusLead}>{payload.message}</p>
  </div>
);

const nonPdfView = (payload: PageCounterNonPdfPayload): React.ReactElement => (
  <div className={styles.statusCardError} role="alert">
    <p className={styles.statusLead}>{payload.guidanceText}</p>
    <p className={styles.statusText}>{payload.selectedFileName}</p>
    <p className={styles.statusText}>{payload.retryLabel}</p>
  </div>
);

const errorView = (payload: PageCounterErrorPayload): React.ReactElement => (
  <div className={styles.statusCardError} role="alert">
    <p className={styles.statusLead}>{payload.message}</p>
    <p className={styles.statusText}>{Option.fold(payload.selectedFileName, () => "No file selected.", (fileName) => fileName)}</p>
    <p className={styles.statusText}>{payload.retryLabel}</p>
  </div>
);

const PageCounterPanel = ({ phase, payload }: PageCounterPanelProps): React.ReactElement => {
  switch (phase) {
    case "idle":
      return idleView;
    case "guidance":
      return guidanceView(payload);
    case "selecting":
      return selectingView(payload);
    case "processing":
      return processingView(payload);
    case "success":
      return successView(payload);
    case "routeMissing":
      return routeMissingView(payload);
    case "nonPdf":
      return nonPdfView(payload);
    case "error":
      return errorView(payload);
  }
};

export default PageCounterPanel;
